import { FC, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  List,
  Stack,
  Typography,
} from "@mui/material";
import DraftsOutlinedIcon from "@mui/icons-material/DraftsOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import __ from "src/utils/Translations";
import EmployeeDetails from "src/Pages/Company/Absences/EmployeeDetails";
import EmployeeCardListItem from "src/Pages/Company/Absences/EmployeeCard/EmployeeCardListItem";
import { IEmployeeDetails } from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import { formatToLocalDate } from "src/utils/dateTime";
import { getEmploymentFormLabels } from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/EmployeeEmployment/constants/employmentForms";

interface IEmployeeCardProps {
  data?: IEmployeeDetails;
}

const EmployeeCard: FC<IEmployeeCardProps> = ({ data }) => {
  const [expanded, setExpanded] = useState(false);

  if (!data) return null;

  const isEmployeeActive = data.active;
  const contactDetails = [
    {
      icon: <DraftsOutlinedIcon />,
      text: data.email,
    },
    {
      icon: <LocalPhoneIcon />,
      text: data.phone,
    },
    {
      icon: <LocationOnOutlinedIcon />,
      text: data.localization,
    },
  ];
  const employmentFormLabels = getEmploymentFormLabels();
  const { nominator, denominator } = data.workingHours || {};

  const employmentDetails = [
    isEmployeeActive && {
      label: __("Data zatrudnienia"),
      value: `od ${formatToLocalDate(data.contractFrom)}`,
    },
    !isEmployeeActive && {
      label: __("Okres zatrudnienia"),
      value: `${formatToLocalDate(data.contractFrom)}-${formatToLocalDate(
        data.contractTo
      )}`,
    },
    {
      label: __("Forma zatrudnienia"),
      value: data.employmentType
        ? employmentFormLabels[data.employmentType]
        : "-",
    },
    {
      label: __("Wymiar etatu"),
      value:
        nominator && denominator ? `${nominator}/${denominator} etatu` : "-",
    },
  ].filter(Boolean) as { label: string; value: string }[];

  return (
    <Accordion
      expanded={expanded}
      onChange={(_event, isExpanded) => setExpanded(isExpanded)}
      disableGutters
      data-testid="absences-employee-card"
    >
      <AccordionSummary
        sx={{
          "& .MuiAccordionSummary-content": {
            alignItems: "center",
          },
        }}
      >
        <EmployeeDetails data={data} showFk />
        <Box sx={{ marginLeft: "auto" }}>
          {!isEmployeeActive && (
            <Chip
              size="small"
              label={__("Konto nieaktywne")}
              sx={{ marginRight: "1.5rem" }}
            />
          )}
          <Button
            variant="text"
            size="large"
            color="inherit"
            endIcon={<ArrowDropDownIcon />}
            sx={{
              color: "text.primary",
              "& .MuiButton-endIcon": {
                transform: expanded ? "rotate(180deg)" : "none",
                transition: "transform .3s ease-in",
              },
            }}
          >
            {expanded ? __("Zwiń") : __("Rozwiń")}
          </Button>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Stack
          direction="row"
          spacing={4}
          divider={<Divider orientation="vertical" flexItem />}
        >
          {employmentDetails.map(({ label, value }) => (
            <Box key={label} sx={{ opacity: isEmployeeActive ? 1 : 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {label}:
              </Typography>
              <Typography variant="subtitle2">{value}</Typography>
            </Box>
          ))}
        </Stack>
        <Divider />
        <List
          dense
          sx={{
            display: "flex",
            flexDirection: "row",
            opacity: isEmployeeActive ? 1 : 0.5,
          }}
        >
          {contactDetails.map(
            ({ icon, text }) =>
              text && (
                <EmployeeCardListItem key={text} text={text} icon={icon} />
              )
          )}
        </List>
      </AccordionDetails>
    </Accordion>
  );
};

export default EmployeeCard;
